import type {
  ChartDataPoint,
  ValueFormatter,
} from '@sparklayer-io/spark-dash-ui/components/ui/line-chart/types';
import { useMemo } from 'react';
import { DEFAULT_CURRENCY } from '../../../constants/currency';
import { formatMoneyCompact, formatMoneyRounded } from '../../../utils/format-money';
import type { PurchaseCount } from '../types';
import type { SalesComparisonReport } from './use-sales-performance';

export type SalesMetric = 'total_val' | 'num_orders' | 'avg_order_val';

function getValue(purchaseCount: PurchaseCount, metric: SalesMetric) {
  switch (metric) {
    case 'total_val':
      return Number(purchaseCount.total.number);
    case 'num_orders':
      return purchaseCount.count;
    case 'avg_order_val':
      return purchaseCount.count === 0
        ? 0
        : Number(purchaseCount.total.number) / purchaseCount.count;
  }
}

function getFormatter(data: PurchaseCount[], metric: SalesMetric): ValueFormatter {
  switch (metric) {
    case 'total_val':
    case 'avg_order_val': {
      const currencyCode = data[0]?.total.currency || DEFAULT_CURRENCY;
      return (value: number, formatType?: 'round' | 'compact') =>
        formatType === 'compact'
          ? formatMoneyCompact(value, currencyCode)
          : formatMoneyRounded(value, currencyCode);
    }
    default:
      return (value: number) => value.toString();
  }
}

export function useSalesChartData(salesPerformance: SalesComparisonReport, metric: SalesMetric) {
  return useMemo(() => {
    const { current, previous } = salesPerformance;
    const result: ChartDataPoint[] = [];

    const currentMap = new Map(
      current.data.map((d) => [new Date(d.date).toDateString(), getValue(d, metric)]),
    );
    const previousMap = new Map(
      previous.data.map((d) => [new Date(d.date).toDateString(), getValue(d, metric)]),
    );

    const iterateDate = new Date(current.start);
    const previousIterateDate = new Date(previous.start);
    const endDate = new Date(current.end);

    while (iterateDate <= endDate) {
      const dateKey = iterateDate.toDateString();
      const prevDateKey = previousIterateDate.toDateString();

      result.push({
        primary: {
          x: new Date(iterateDate),
          y: currentMap.get(dateKey) ?? 0,
        },
        secondary: {
          x: new Date(previousIterateDate),
          y: previousMap.get(prevDateKey) ?? 0,
        },
      });

      iterateDate.setDate(iterateDate.getDate() + 1);
      previousIterateDate.setDate(previousIterateDate.getDate() + 1);
    }

    return {
      data: result,
      valueFormatter: getFormatter(current.data, metric),
    };
  }, [salesPerformance, metric]);
}
